import { Fragment, forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Highlight } from '../highlight/highlight';
import type { HighlighterPropsInterface } from './types/highlighter.types';
import { useHighlighterChunks } from './use-highlighter-chunks';

/**
 * A utility component that will break a text string into matching pieces. This component provides
 * no style on its own.
 *
 * @since 8.28.0
 */
export const Highlighter = forwardRef<HTMLSpanElement, HighlighterPropsInterface>(
  (props: HighlighterPropsInterface, forwardedRef) => {
    const {
      className,
      css: cssProp,
      pattern,
      renderHighlight = ({ key, ...highlightProps }) => <Highlight key={key} {...highlightProps} />,
      text,
      ...rest
    } = props;

    const chunks = useHighlighterChunks({ text, pattern });

    const content = useMemo(
      () =>
        chunks.map((chunk) =>
          chunk.highlight ? (
            renderHighlight({ key: chunk.key, children: chunk.text })
          ) : (
            <Fragment key={chunk.key}>{chunk.text}</Fragment>
          ),
        ),
      [chunks, renderHighlight],
    );

    return (
      <span
        ref={forwardedRef}
        data-c11n-component="Highlighter"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', css(cssProp), className)}
        {...rest}
      >
        {content}
      </span>
    );
  },
);

Highlighter.displayName = 'Highlighter';
